/** Per-instance folders on Mitarbeiter → Dokumente. */

export const EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY = 'other';

export type EmployeeDocumentTypeRow = {
  key: string;
  label: string;
  sort_order: number;
  is_enabled: boolean;
  is_system: boolean;
};

export const DEFAULT_EMPLOYEE_DOCUMENT_TYPES: EmployeeDocumentTypeRow[] = [
  { key: 'employment_contract', label: 'Arbeitsvertrag', sort_order: 0, is_enabled: true, is_system: true },
  { key: 'timesheet', label: 'Stundenzettel', sort_order: 1, is_enabled: true, is_system: true },
  { key: 'vacation_request', label: 'Urlaubsanträge', sort_order: 2, is_enabled: true, is_system: true },
  { key: 'id_card', label: 'Personalausweis', sort_order: 3, is_enabled: true, is_system: true },
  { key: 'sick_note', label: 'Krankschreibung', sort_order: 4, is_enabled: true, is_system: true },
  { key: 'drivers_license_scan', label: 'Führerschein', sort_order: 5, is_enabled: true, is_system: true },
  { key: 'certificate', label: 'Zertifikat', sort_order: 6, is_enabled: true, is_system: true },
  { key: EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY, label: 'Sonstiges', sort_order: 7, is_enabled: true, is_system: true },
];

export const DEFAULT_EMPLOYEE_DOCUMENT_TYPE_KEYS = DEFAULT_EMPLOYEE_DOCUMENT_TYPES.map((row) => row.key);

const SYSTEM_KEY_SET = new Set(DEFAULT_EMPLOYEE_DOCUMENT_TYPE_KEYS);

export function isSystemEmployeeDocumentTypeKey(key: string): boolean {
  return SYSTEM_KEY_SET.has(key);
}

export function fallbackEmployeeDocumentTypes(): EmployeeDocumentTypeRow[] {
  return DEFAULT_EMPLOYEE_DOCUMENT_TYPES.map((row) => ({ ...row }));
}

export function slugEmployeeDocumentTypeKey(label: string, idx: number): string {
  const base = label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return base || `custom_${idx}`;
}

export function uniqueEmployeeDocumentTypeKey(label: string, idx: number, taken: Set<string>): string {
  const base = slugEmployeeDocumentTypeKey(label, idx);
  if (!taken.has(base) && !SYSTEM_KEY_SET.has(base)) return base;
  let n = 2;
  let candidate = `${base}_${n}`;
  while (taken.has(candidate) || SYSTEM_KEY_SET.has(candidate)) {
    n += 1;
    candidate = `${base}_${n}`;
  }
  return candidate;
}

function parseIncomingRow(raw: unknown): {
  key: string;
  label: string;
  sort_order: number | undefined;
  is_enabled: boolean;
} | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const key = String(row.key ?? row.id ?? '').trim();
  const label = String(row.label ?? '').trim();
  const sortOrder = typeof row.sort_order === 'number' && Number.isFinite(row.sort_order) ? row.sort_order : undefined;
  const isEnabled = row.is_enabled !== false;
  return { key, label, sort_order: sortOrder, is_enabled: isEnabled };
}

export function parseEmployeeDocumentTypeRows(raw: unknown): EmployeeDocumentTypeRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: EmployeeDocumentTypeRow[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < raw.length; i += 1) {
    const item = raw[i];
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const key = String(row.key ?? row.id ?? '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    rows.push({
      key,
      label: String(row.label ?? key).trim() || key,
      sort_order: typeof row.sort_order === 'number' ? row.sort_order : i,
      is_enabled: row.is_enabled !== false,
      is_system: row.is_system === true || isSystemEmployeeDocumentTypeKey(key),
    });
  }
  return rows.sort((a, b) => a.sort_order - b.sort_order);
}

export function enabledEmployeeDocumentTypes(rows: EmployeeDocumentTypeRow[]): EmployeeDocumentTypeRow[] {
  return rows.filter((row) => row.is_enabled);
}

export function employeeDocumentTypeLabel(rows: EmployeeDocumentTypeRow[], key: string | null | undefined): string {
  const trimmed = String(key ?? '').trim();
  if (!trimmed) return 'Sonstiges';
  const found = rows.find((row) => row.key === trimmed);
  if (found) return found.label;
  const fallback = DEFAULT_EMPLOYEE_DOCUMENT_TYPES.find((row) => row.key === trimmed);
  return fallback?.label ?? trimmed;
}

export function employeeDocumentFilterOptions(
  rows: EmployeeDocumentTypeRow[],
): Array<{ id: 'all' | string; label: string }> {
  return [
    { id: 'all', label: 'Alle' },
    ...enabledEmployeeDocumentTypes(rows).map((row) => ({ id: row.key, label: row.label })),
  ];
}

export type PrepareEmployeeDocumentTypesSaveResult =
  | { ok: true; next: EmployeeDocumentTypeRow[]; removedKeys: string[] }
  | { ok: false; error: string };

/** Merge a PUT payload with existing rows. System types cannot be removed; `other` stays enabled. */
export function prepareEmployeeDocumentTypesSave(
  existing: EmployeeDocumentTypeRow[],
  incomingRaw: unknown,
): PrepareEmployeeDocumentTypesSaveResult {
  if (!Array.isArray(incomingRaw) || incomingRaw.length < 1) {
    return { ok: false, error: 'Mindestens ein Dokumenttyp muss bestehen.' };
  }

  const existingByKey = new Map(existing.map((row) => [row.key, row]));
  const taken = new Set<string>();
  const next: EmployeeDocumentTypeRow[] = [];

  for (let idx = 0; idx < incomingRaw.length; idx += 1) {
    const parsed = parseIncomingRow(incomingRaw[idx]);
    if (!parsed) continue;
    const existingRow = parsed.key ? existingByKey.get(parsed.key) : undefined;
    const label =
      parsed.label || existingRow?.label || (parsed.key && SYSTEM_KEY_SET.has(parsed.key)
        ? DEFAULT_EMPLOYEE_DOCUMENT_TYPES.find((row) => row.key === parsed.key)?.label
        : null) || 'Neuer Typ';
    let key: string;
    if (existingRow) {
      key = existingRow.key;
    } else if (parsed.key && isSystemEmployeeDocumentTypeKey(parsed.key) && !taken.has(parsed.key)) {
      key = parsed.key;
    } else {
      key = uniqueEmployeeDocumentTypeKey(label, idx, taken);
    }
    if (taken.has(key)) continue;
    taken.add(key);
    const isSystem = isSystemEmployeeDocumentTypeKey(key) || Boolean(existingRow?.is_system);
    const isEnabled = key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY ? true : parsed.is_enabled;
    next.push({
      key,
      label: label.slice(0, 80),
      sort_order: idx,
      is_enabled: isEnabled,
      is_system: isSystem,
    });
  }

  if (next.length < 1) {
    return { ok: false, error: 'Mindestens ein Dokumenttyp muss bestehen.' };
  }

  for (const system of DEFAULT_EMPLOYEE_DOCUMENT_TYPES) {
    if (taken.has(system.key)) continue;
    const previous = existingByKey.get(system.key) ?? system;
    next.push({
      ...previous,
      is_system: true,
      is_enabled: system.key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY ? true : previous.is_enabled,
      sort_order: next.length,
    });
    taken.add(system.key);
  }

  const other = next.find((row) => row.key === EMPLOYEE_DOCUMENT_TYPE_OTHER_KEY);
  if (other) other.is_enabled = true;

  const nextKeys = new Set(next.map((row) => row.key));
  const removedKeys = existing
    .filter((row) => !row.is_system && !isSystemEmployeeDocumentTypeKey(row.key) && !nextKeys.has(row.key))
    .map((row) => row.key);

  return {
    ok: true,
    next: next.map((row, idx) => ({ ...row, sort_order: idx })),
    removedKeys,
  };
}
